import { faColumns } from "@fortawesome/free-solid-svg-icons";
import { Radio, Switch } from "antd";
import { DownArr, RightIcon } from "components/shared/icons";
import PickIcon from "components/shared/PickIcon";
import ThemePalettePicker from "./ThemePalettePicker";
import i18next, { t } from "i18next";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DIRECTION, LANGUAGE } from "services/general";
import {
  isPushEnabledInSettings,
  isPushSupported,
  pushPermission,
  requestPushPermission,
  setPushEnabledInSettings,
} from "services/push-notifications";

const Settings = ({
  isOpen,
  setIsOpen,
  setIsDark,
  isDark,
  setNavPosition,
  navPosition,
  setSidebar,
  sidebar,
}) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [isLngSelectOpen, setIsLngSelectOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem("default-lg") || "en"
  );

  const [notificationsSupported, setNotificationsSupported] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    isPushEnabledInSettings
  );

  useEffect(() => {
    if (!isPushSupported()) {
      setNotificationsSupported(false);
    }
  }, []);

  const handleNotificationsChange = async (checked) => {
    // Asked before anything is awaited: Firefox and Safari only prompt while the
    // click's user activation is still live.
    const needsPrompt = checked && pushPermission() === "default";
    const request = needsPrompt ? requestPushPermission() : null;

    setNotificationsEnabled(checked);
    setPushEnabledInSettings(checked);

    if (request) {
      await request;
      // Re-announce, so useFcmToken registers now that permission is decided.
      setPushEnabledInSettings(checked);
    }
  };

  const isNotificationBlocked = pushPermission() === "denied";

  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "ar", name: "العربية", flag: "🇸🇦" },
  ];

  const handleClose = () => {
    setIsOpen(false);
  };

  const changeLng = (languageCode) => {
    localStorage.setItem("default-lg", languageCode);
    i18next.changeLanguage(languageCode);
    setSelectedLanguage(languageCode);
    setIsLngSelectOpen(false);

    const newPath = pathname.replace(/^\/[^/]+/, `/${languageCode}`);
    navigate(newPath + search);
    window.location.reload();
  };

  const selectedLang = languages.find((lang) => lang.code === selectedLanguage);

  return (
    <>
      {/* Background overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[901]"
          onClick={handleClose}
        />
      )}

      {/* Sliding panel */}
      <div
        className={`fixed top-0 h-full overflow-y-auto bg-surface z-[902] transition-transform duration-300 ease-in-out
          w-full lg:w-[30%] 
          ${DIRECTION === "rtl" ? "left-0" : "right-0"}
          ${isOpen ? "translate-x-0" : DIRECTION === "rtl" ? "-translate-x-full" : "translate-x-full"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-9 pb-5 border-b border-border-light">
          <h1 className="text-text-strong text-lg font-semibold">
            {t("settings.title")}
          </h1>
          <img
            src="/icons/close.svg"
            alt="close"
            onClick={handleClose}
            className="cursor-pointer"
          />
        </div>

        {/* Content */}
        <ul className="st mb-20">
          {/* Theme palette */}
          <li className="p-4 border-b border-border-light">
            <div className="flex items-start gap-2.5 mb-3">
              <img src="/icons/color.svg" alt="" />
              <div>
                <span className="text-sm text-text-strong">
                  {t("settings.palette")}
                </span>
                <p className="text-xs text-text-muted mt-0.5">
                  {t("settings.paletteHint")}
                </p>
              </div>
            </div>
            <ThemePalettePicker />
          </li>

          <li className="flex items-center justify-between p-4 border-b border-border-light">
            <div className="flex items-center gap-2.5">
              <PickIcon width={"24px"} height={"24px"} iconName={faColumns} />
              <span className="text-sm text-text-strong">
                {t("settings.position")}
              </span>
            </div>

            <Radio.Group
              onChange={(e) => {
                const value = e.target.value;
                setNavPosition(value);
                localStorage.setItem("navPosition", value);
              }}
              value={navPosition}
              optionType="button"
              buttonStyle="solid"
              className="border rounded-lg"
            >
              <Radio.Button value="top">{t("settings.top")}</Radio.Button>
              <Radio.Button value="right">
                {t(LANGUAGE === "ar" ? "settings.right" : "settings.left")}
              </Radio.Button>
            </Radio.Group>
          </li>

          {/* Resize */}
          {navPosition === "right" && window.innerWidth >= 1024 ? (
            <li className="flex items-center justify-between p-4 border-b border-border-light xsm-switch">
              <div className="flex items-center gap-2.5">
                <img src="/icons/resize.svg" alt="" />
                <span className="text-sm text-text-strong">
                  {t("settings.resize")}
                </span>
              </div>
              <Switch
                value={sidebar}
                onChange={(v) => {
                  setSidebar(v);
                  localStorage.setItem("sidebar", v ? 1 : 0);
                }}
              />
            </li>
          ) : (
            ""
          )}

          {/* Dark mode */}
          <li className="flex items-center justify-between p-4 border-b border-border-light xsm-switch">
            <div className="flex items-center gap-2.5">
              <img src="/icons/dark.svg" alt="" />
              <span className="text-sm text-text-strong">
                {t("settings.dark")}
              </span>
            </div>
            <Switch
              onChange={(v) => {
                setIsDark(v);
                localStorage.setItem("isDark", v ? 1 : 0);
              }}
              value={isDark}
            />
          </li>

          {/* Push Notifications */}
          <li className="flex items-center justify-between p-4 border-b border-border-light xsm-switch">
            <div className="flex items-center gap-2.5">
              <img src="/icons/notify-icon.svg" alt="" />
              <span className="text-sm text-text-strong">
                {t("settings.notifications")}
              </span>
            </div>
            <Switch
              onChange={handleNotificationsChange}
              checked={notificationsEnabled && !isNotificationBlocked}
              disabled={!notificationsSupported || isNotificationBlocked}
            />
          </li>

          {/* Language */}
          <li className="flex items-center justify-between p-4 border-b border-border-light">
            <div className="flex items-center gap-2.5">
              <img src="/icons/lng2.svg" alt="" />
              <span className="text-sm text-text-strong">
                {t("settings.lng")}
              </span>
            </div>

            <div className="relative">
              <button
                onClick={() => setIsLngSelectOpen(!isLngSelectOpen)}
                className="flex items-center gap-2 px-3 py-1 bg-surface border border-border-light rounded-lg min-w-[100px] hover:border-border-muted transition-colors"
              >
                <span>{selectedLang?.flag}</span>
                <span className="text-sm text-text-strong flex-1 text-start">
                  {selectedLang?.name}
                </span>
                <DownArr isLngSelectOpen={isLngSelectOpen} />
              </button>

              {isLngSelectOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsLngSelectOpen(false)}
                  />
                  <div className="absolute end-0 top-full mt-1 w-full bg-surface border border-border-light rounded-lg shadow-lg z-20 overflow-hidden">
                    {languages.map((language) => (
                      <button
                        key={language.code}
                        onClick={() => changeLng(language.code)}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-start hover:bg-surface-muted transition-colors ${
                          selectedLanguage === language.code
                            ? "bg-fill-subtle"
                            : ""
                        }`}
                      >
                        <span>{language.flag}</span>
                        <span className="text-sm text-text-strong">
                          {language.name}
                        </span>
                        {selectedLanguage === language.code && <RightIcon />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </li>
        </ul>
      </div>
    </>
  );
};

export default Settings;
